import Image from 'next/image';
import { HtmlPttV1 } from '@adapters/prismic/types/plp';
import Heading from '@components/ui/Heading';
import { FEATURE_FLAG_PTT_VIDEO } from '@config/feature-flag';
import dynamic from 'next/dynamic';
import { useLocale } from '@hooks/useLocale';
import { addLocaleToUrl } from '@utils/url';

const VideoButton = dynamic(() => import('../VideoButton'), {
  ssr: false,
});
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import styles from '../PTT.module.scss';

interface PTTOneProps {
  ptt: HtmlPttV1;
}

const PTTOne = ({ ptt }: PTTOneProps) => {
  const locale = useLocale();
  const {
    logo,
    image,
    label,
    title,
    body,
    link,
    videoId,
    videoHeight,
    videoWidth,
  } = ptt?.product_tile || {};

  const handleClick = () => {
    if (typeof window !== 'undefined') {
      trackPromotionEvent({
        eventName: PromotionEventName.selectPromotion,
        page: {
          path: window.location.pathname,
          page_type: determinePageType(),
        },
        interaction: {
          coop_brand: ptt?.product_tile['co-op_brand_attribute'] || null,
          interaction_location: InteractionLocation.PTT,
        },
        ecommerce: {
          creative_name: `link,${ptt?.product_tile?.link?.url || ''}`,
          creative_slot: buildCreativeSlotName({
            sliceIndex: ptt?.position,
            interactionLocation: InteractionLocation.PTT,
            itemIndex: ptt?.treatment,
          }),
          promotion_id: ptt?.product_tile?.title || '',
          promotion_name: ptt?.product_tile?.link?.text || '',
          isVideo: Boolean(ptt?.product_tile?.videoId),
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      {logo && logo?.url && logo?.alt && (
        <div className={styles.logoContainer}>
          <Image
            src={logo.url}
            alt={logo.alt}
            fill
            sizes="100vw"
            style={{
              objectFit: 'contain',
              objectPosition: 'left',
            }}
          />
        </div>
      )}
      <div className={styles.imageContainer}>
        {image && image.url && image.alt && (
          <Image
            src={image.url}
            alt={image.alt}
            fill
            sizes="100vw"
            style={{
              objectFit: 'cover',
            }}
          />
        )}
      </div>
      <div>
        {label && <p className={styles.label}>{label}</p>}
        {title && (
          <Heading semanticLevel="h3" styleLevel="3" className={styles.title}>
            {title}
          </Heading>
        )}
        {body && <p className={styles.body}>{body}</p>}
      </div>
      {link && link.url && (
        <a
          href={addLocaleToUrl(link.url || '', locale)}
          className={styles.link}
          onClick={handleClick}
        >
          {link.text}
        </a>
      )}
      {FEATURE_FLAG_PTT_VIDEO && link && link.text && videoId && (
        <VideoButton
          videoId={videoId}
          videoHeight={videoHeight}
          videoWidth={videoWidth}
          text={link.text}
        />
      )}
    </div>
  );
};

export default PTTOne;
